import React, { useMemo } from 'react';
import { Table, Segment, Header, Icon, Label, Message } from 'semantic-ui-react';
import { ProtobufSchemaParser } from './protobufSchemaParser';
import { TYPES } from './protobufDecoder';
import { bufferToPrettyHex } from './hexUtils';
import { useI18n } from './i18n';

// 格式化字段值显示
function formatFieldValue(part, fieldInfo) {
  if (!fieldInfo) {
    // 如果没有字段信息，显示原始数据
    return (
      <div>
        <Label color="grey" size="mini">Unknown Field {part.index}</Label>
        <div style={{ marginTop: '4px' }}>
          {part.type === TYPES.VARINT && (
            <span style={{ fontFamily: 'monospace' }}>{part.value}</span>
          )}
          {part.type === TYPES.LENDELIM && (
            <div>
              <Label color="blue" size="mini">Bytes</Label>
              <div style={{ 
                fontFamily: 'monospace', 
                fontSize: '11px',
                backgroundColor: '#f8f9fa',
                padding: '4px',
                marginTop: '4px',
                borderRadius: '2px'
              }}>
                {bufferToPrettyHex(part.value)}
              </div>
            </div>
          )}
          {part.type === TYPES.FIXED32 && (
            <span style={{ fontFamily: 'monospace' }}>{bufferToPrettyHex(part.value)}</span>
          )}
          {part.type === TYPES.FIXED64 && (
            <span style={{ fontFamily: 'monospace' }}>{bufferToPrettyHex(part.value)}</span>
          )}
        </div>
      </div>
    );
  }

  // 根据字段类型和值进行格式化
  const { type, name, repeated } = fieldInfo;
  
  let displayValue = '';
  let valueType = '';

  switch (part.type) {
    case TYPES.VARINT:
      const intValue = parseInt(part.value);
      if (type === 'bool') {
        displayValue = intValue ? 'true' : 'false';
        valueType = 'bool';
      } else if (type === 'int32' || type === 'uint32' || type === 'sint32') {
        displayValue = intValue.toString();
        valueType = type;
      } else if (type === 'int64' || type === 'uint64' || type === 'sint64') {
        displayValue = intValue.toString();
        valueType = type;
      } else {
        displayValue = intValue.toString();
        valueType = 'varint';
      }
      break;

    case TYPES.LENDELIM:
      if (type === 'string') {
        try {
          const decoder = new TextDecoder('utf-8', { fatal: false });
          const stringValue = decoder.decode(part.value);
          displayValue = stringValue;
          valueType = 'string';
        } catch {
          displayValue = bufferToPrettyHex(part.value);
          valueType = 'bytes';
        }
      } else if (type === 'bytes') {
        displayValue = bufferToPrettyHex(part.value);
        valueType = 'bytes';
      } else {
        // 可能是嵌套的 message
        displayValue = bufferToPrettyHex(part.value);
        valueType = 'message';
      }
      break;

    case TYPES.FIXED32:
      if (type === 'float') {
        // Protobuf fixed32 is little-endian
        const view = new DataView(part.value.buffer, part.value.byteOffset, 4);
        displayValue = view.getFloat32(0, true).toString();
        valueType = 'float';
      } else if (type === 'fixed32' || type === 'sfixed32') {
        displayValue = bufferToPrettyHex(part.value);
        valueType = type;
      } else {
        displayValue = bufferToPrettyHex(part.value);
        valueType = 'fixed32';
      }
      break;

    case TYPES.FIXED64:
      if (type === 'double') {
        // Protobuf fixed64 is little-endian
        const view = new DataView(part.value.buffer, part.value.byteOffset, 8);
        displayValue = view.getFloat64(0, true).toString();
        valueType = 'double';
      } else if (type === 'fixed64' || type === 'sfixed64') {
        displayValue = bufferToPrettyHex(part.value);
        valueType = type;
      } else {
        displayValue = bufferToPrettyHex(part.value);
        valueType = 'fixed64';
      }
      break;

    default:
      displayValue = bufferToPrettyHex(part.value);
      valueType = 'unknown';
  }

  return (
    <div>
      <div style={{ marginBottom: '4px' }}>
        <Label color="green" size="mini">{name}</Label>
        <Label color="blue" size="mini">{valueType}</Label>
        {fieldInfo.optional && <Label color="yellow" size="mini">optional</Label>}
        {repeated && <Label color="orange" size="mini">repeated</Label>}
      </div>
      <div style={{ 
        fontFamily: 'monospace',
        backgroundColor: '#f8f9fa',
        padding: '4px',
        borderRadius: '2px',
        fontSize: '12px'
      }}>
        {displayValue}
      </div>
    </div>
  );
}

function SchemaBasedDisplay({ decodedResult, schemaText, selectedMessage }) {
  const { t } = useI18n();
  const parser = useMemo(() => new ProtobufSchemaParser(), []);
  
  const schema = useMemo(() => {
    if (!schemaText.trim()) return null;
    return parser.parseSchema(schemaText);
  }, [schemaText, parser]);

  const validation = useMemo(() => {
    if (!schemaText.trim()) return { valid: true, errors: [] };
    return parser.validateSchema(schemaText);
  }, [schemaText, parser]);

  const formattedParts = useMemo(() => {
    if (!decodedResult || !schema || !selectedMessage) {
      return decodedResult?.parts || [];
    }

    const messageFields = schema.get(selectedMessage);
    if (!messageFields) {
      return decodedResult.parts;
    }

    // 重新解析并格式化字段
    return decodedResult.parts.map(part => {
      const fieldInfo = messageFields.fields.find(f => f.number === part.index);
      return {
        ...part,
        fieldInfo
      };
    });
  }, [decodedResult, schema, selectedMessage]);

  if (!schemaText.trim()) {
    return (
      <Message info>
        <Message.Header>{t('schema_no_schema')}</Message.Header>
        <p>{t('schema_no_schema_help')}</p>
      </Message>
    );
  }

  if (!validation.valid) {
    return (
      <Message negative>
        <Message.Header>{t('schema_validation_error')}</Message.Header>
        <ul>
          {validation.errors.map((error, index) => (
            <li key={index}>{error}</li>
          ))}
        </ul>
      </Message>
    );
  }

  const availableMessages = schema ? Array.from(schema.keys()) : [];

  if (availableMessages.length === 0) {
    return (
      <Message warning>
        <Message.Header>{t('schema_no_messages_found')}</Message.Header>
        <p>{t('schema_no_valid_messages')}</p>
        <p>Please ensure your schema contains message definitions like:</p>
        <ul>
          <li><code>message testmsg {'{}'}</code> - Empty message</li>
          <li><code>message testmsg {'{ optional uint32 field1 = 1; }'}</code> - Optional field</li>
          <li><code>message testmsg {'{ uint32 field1 = 1; // comment }'}</code> - With comments</li>
          <li><code>message testmsg {'{ repeated string tags = 2; }'}</code> - Repeated field</li>
        </ul>
        <p>Current schema content (first 200 chars):</p>
        <pre style={{ 
          backgroundColor: '#f5f5f5', 
          padding: '8px', 
          borderRadius: '4px',
          fontSize: '12px',
          maxHeight: '100px',
          overflow: 'auto'
        }}>
          {schemaText.substring(0, 200)}...
        </pre>
      </Message>
    );
  }

  if (!selectedMessage || !schema.has(selectedMessage)) {
    return (
      <Message warning>
        <Message.Header>{t('schema_select_message')}</Message.Header>
        <p>{t('schema_select_message_help')}</p>
        <p>{t('schema_available_messages', { messages: availableMessages.join(', ') })}</p>
      </Message>
    );
  }

  const messageFields = schema.get(selectedMessage);

  return (
    <Segment>
      <Header as="h5">
        <Icon name="code" color="green" />
        {t('schema_based_display')}: {selectedMessage}
        <Header.Subheader>
          Last updated: {new Date().toLocaleTimeString()}
        </Header.Subheader>
      </Header>
      
      <div style={{ marginBottom: '12px' }}>
        <Label color="teal" size="small">
          <Icon name="info" />
          {t('schema_fields_defined', { count: messageFields.fields.length })}
        </Label>
        <Label color="blue" size="small">
          <Icon name="code" />
          {t('schema_fields_found', { count: decodedResult?.parts?.length || 0 })}
        </Label>
      </div>

      <div style={{ 
        width: '100%',
        overflowX: 'auto'
      }}>
        <Table
          celled
          compact
          size="small"
          style={{
            margin: 0,
            width: '100%',
            tableLayout: 'fixed',
          }}
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell style={{ width: '64px' }}>{t('table_field_number')}</Table.HeaderCell>
              <Table.HeaderCell style={{ width: '180px' }}>{t('table_field_name')}</Table.HeaderCell>
              <Table.HeaderCell style={{ width: '120px' }}>{t('table_type')}</Table.HeaderCell>
              <Table.HeaderCell style={{ width: '120px' }}>{t('table_wire_type')}</Table.HeaderCell>
              <Table.HeaderCell>{t('table_value')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {formattedParts.map((part, i) => (
              <Table.Row key={i}>
                <Table.Cell style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                  {part.index}
                </Table.Cell>
                <Table.Cell style={{ wordBreak: 'break-word' }}>
                  {part.fieldInfo ? (
                    <div>
                      <strong>{part.fieldInfo.name}</strong>
                      {part.fieldInfo.repeated && (
                        <Label color="orange" size="mini" style={{ marginLeft: '4px' }}>
                          repeated
                        </Label>
                      )}
                    </div>
                  ) : (
                    <span style={{ color: '#999' }}>Unknown</span>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {part.fieldInfo ? (
                    <Label color="blue" size="mini">{part.fieldInfo.type}</Label>
                  ) : (
                    <span style={{ color: '#999' }}>Unknown</span>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <Label color="grey" size="mini">
                    {part.type === TYPES.VARINT ? 'VARINT' :
                     part.type === TYPES.LENDELIM ? 'LENDELIM' :
                     part.type === TYPES.FIXED32 ? 'FIXED32' :
                     part.type === TYPES.FIXED64 ? 'FIXED64' : 'UNKNOWN'}
                  </Label>
                </Table.Cell>
                <Table.Cell style={{ wordBreak: 'break-word' }}>
                  {formatFieldValue(part, part.fieldInfo)}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
    </Segment>
  );
}

export default SchemaBasedDisplay; 